import plural from 'plural-ru';

import { FormatUtils } from 'app/utils/FormatUtils';

interface Rule {
    /**
     * Значение валидации.
     */
    value: any;

    /**
     * Сообщение ошибки.
     */
    message: string;
}

export enum ValidationErrorMessage {
    REQUIRED = 'Обязательно заполните',
}

/**
 * Возвращает объект вида {@link Rule}
 *
 * @param value значение валидации
 * @param message сообщение ошибки
 */
const getRule = (value: any, message: string): Rule => {
    return { value, message };
};

/**
 * Класс содержит набор методов с правилами и сообщения к ним, описанными в спецификации.
 */
export class ValidationRules {
    /**
     * Обязательность заполнения.
     *
     * @param message сообщение ошибки
     */
    public static readonly required = (message?: string): Rule => {
        return getRule(
            true,
            message ? message : ValidationErrorMessage.REQUIRED,
        );
    };

    /**
     * Максимальное кол-во символов для поля.
     *
     * @param maxLength максимальное кол-во символов
     * @param message сообщение ошибки
     */
    public static readonly maxLength = (
        maxLength: number,
        message?: string,
    ): Rule => {
        return getRule(
            maxLength,
            message
                ? message
                : `Максимум ${plural(
                      maxLength,
                      '%d символ',
                      '%d символа',
                      '%d символов',
                  )}`,
        );
    };

    /**
     * Минимальное кол-во символов для поля.
     *
     * @param minLength минимальное кол-во символов
     * @param message сообщение ошибки
     */
    public static readonly minLength = (
        minLength: number,
        message?: string,
    ): Rule => {
        return getRule(
            minLength,
            message
                ? message
                : `Минимум ${plural(
                      minLength,
                      '%d символ',
                      '%d символа',
                      '%d символов',
                  )}`,
        );
    };

    /**
     * Минимальное числовое значение для поля
     *
     * @param min минимальное значение
     * @param message сообщение ошибки
     */
    public static readonly min = (min: number, message?: string): Rule => {
        return getRule(
            min,
            message
                ? message
                : `Можно ввести значение большее или равное ${FormatUtils.toIntegerString(
                      min,
                  )}`,
        );
    };

    /**
     * Максимальное числовое значение для поля
     *
     * @param max минимальное значение
     * @param message сообщение ошибки
     */
    public static readonly max = (max: number, message?: string): Rule => {
        return getRule(
            max,
            message
                ? message
                : `Можно ввести значение меньшее или равное ${FormatUtils.toIntegerString(
                      max,
                  )}`,
        );
    };

    /**
     * Email-формат
     *
     * Доступный ввод для имени пользователя:
     *
     * - A...Z – прописные латинские буквы;
     * - a...z – строчные латинские буквы;
     * - цифры
     * - знаки _ - .
     // * - знаки ! # $ % & ' * + - / =? ^ _ ` { | } ~
     // * - точку, за исключением первого и последнего знака, которая не может повторятся (не может быть две точки подряд)
     *
     * Доступный ввод для имени хоста состоит из нескольких компонентов, разделённых точкой. Компоненты:
     *
     * - латинские буквы,
     * - цифры, дефисы, точки
     // * - цифры и дефисы (дефисы не могут быть в начале или в конце компонента).
     *
     * @param message сообщение ошибки
     */
    public static readonly email = (message?: string): Rule => {
        return getRule(
            ValidationRules.emailRegex,
            message
                ? message
                : 'Email введен некорректно. Пример: example@mail.com',
        );
    };

    /**
     * Указаны допустимые символы, и введен символ, который не входит в них.
     *
     * @param allowedCharacters допустимые символы
     * @param message сообщение ошибки
     */
    public static readonly allowedCharacters = (
        allowedCharacters: RegExp,
        message?: string,
    ): Rule => {
        return getRule(
            allowedCharacters,
            message ? message : 'Введены недопустимые символы',
        );
    };

    /**
     * Допустимо значение, состоящее из определенного количества цифр
     *
     * @param allowedLength количество цифр в числе
     * @param message сообщение ошибки
     */
    public static readonly exactNumberLength = (
        allowedLength: number,
        message?: string,
    ): Rule => {
        const pattern = new RegExp('^\\d{' + allowedLength + '}$');

        return getRule(
            pattern,
            message
                ? message
                : `Длина должна составлять ${allowedLength} символов`,
        );
    };

    /**
     * Допустимо значение, состоящее из определенного количества букв и цифр
     *
     * @param allowedChars количество букв
     * @param allowedDigits количество цифр
     * @param message сообщение ошибки
     */
    public static readonly exactCharsAndNumbersLength = (
        allowedChars: number,
        allowedDigits: number,
        message?: string,
    ): Rule => {
        const pattern = new RegExp(
            '^[A-Za-z]{' + allowedChars + '}\\d{' + allowedDigits + '}$',
        );

        return getRule(
            pattern,
            message
                ? message
                : `Должен состоять из ${allowedChars} латинских букв и ${allowedDigits} цифр`,
        );
    };

    public static readonly unpMessage = 'Введите 9 символов УНП';

    public static readonly unpSelfEmployedMessage =
        'Введите 9 символов УНП в формате AB1234567 или 123456789';

    /**
     * Допустимо значение, состоящее из определенного количества букв и цифр
     *
     * @param message сообщение ошибки
     */
    public static readonly unpSelfEmployedRule = (message?: string): Rule => {
        const pattern = new RegExp('^([A-Za-z]{2}|\\d{2})\\d{7}$');

        return getRule(
            pattern,
            message ? message : ValidationRules.unpSelfEmployedMessage,
        );
    };

    public static readonly unp = ValidationRules.exactNumberLength(
        9,
        ValidationRules.unpMessage,
    );

    public static readonly unpSelfEmployed =
        ValidationRules.unpSelfEmployedRule(
            ValidationRules.unpSelfEmployedMessage,
        );

    /**
     * RegExp для e-mail
     */
    public static readonly emailRegex =
        /^[a-zA-Z0-9!#$%&'*+\/\]=?^_`{}~-]+(?:\.[a-zA-Z0-9!#$%&'*+\/\]=?^_`{}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)\.[a-zA-Z]{2,4}$/;

    /**
     * RegExp для номера телефона в формате +375 (ХХ) ХХХ-ХХ-ХХ
     */
    public static readonly phoneNumberRegex =
        /^(\+375) \((\d{2})\) (\d{3})-(\d{2})-(\d{2})$/;

    /**
     * RegExp для УНП, определяющий однозначно формат самозанятого
     */
    public static readonly unpSelfEmployedWithCharsRegex = /^[A-Za-z]{2}\d{7}$/;

    /**
     * Допустимо значение номера телефона в формате +375 (ХХ) ХХХ-ХХ-ХХ
     * @param message сообщение ошибки
     */
    public static readonly phoneNumber = (message?: string): Rule => {
        const regex = ValidationRules.phoneNumberRegex;
        return getRule(regex, message ? message : 'Поле заполнено некорректно');
    };

    /**
     * Набор дополнительных проверок на правильность введенного номера:
     * operator - существование введенного кода оператора
     */
    public static readonly phoneNumberValidations = (): {
        [name: string]: (value: string) => string;
    } => {
        return {
            operator: (phoneNumber: string) =>
                !/\((25|29|33|44)\)/.test(phoneNumber)
                    ? 'Код оператора заполнен некорректно'
                    : null,
        };
    };
}

/**
 * Валидация на допустимые символы для полей фамилии/имени.
 */
export const getFIOAllowedCharactersValidation = (): [RegExp, string] => {
    /**
     * Допускает:
     * - латинские буквы в верхнем регистре
     * - символ пробела
     * - символ дефиса ( - )
     */
    const value = /^[A-Z- ']+$/;

    const message =
        'Допустимые символы: буквы латиницей (A, b, c), дефис, пробел, апостроф';

    return [value, message];
};

/**
 * Валидация на допустимые символы для полей фамилии/имени.
 */
export const getFIOAllowedCharactersForFirstAndLastSymbolValidation = (): [
    RegExp,
    string,
] => {
    /**
     * Допускает:
     * - латинские буквы в верхнем регистре в начале и в конце стороки
     */
    const value = /^[A-Z]$|^[A-Z].*?[A-Z]$/;

    const message =
        'Первый и последний символ должны быть буквой латинского алфавита';

    return [value, message];
};

/**
 * Валидация на максимальное/минимальное кол-во символов для полей фамилии/имени.
 */
const getFIOLengthValidation = (props?: {
    length?: number;
    message?: string;
    isMax?: boolean;
}): [number, string] => {
    const length = props?.length || 23;
    const validationOrder = props?.isMax ? 'Максимум' : 'Минимум';

    const message =
        props?.message ||
        `${validationOrder} ${plural(
            length,
            '%d символ',
            '%d символа',
            '%d символов',
        )}`;

    return [length, message];
};

/**
 * Валидация на максимальное кол-во символов для полей фамилии/имени.
 */
export const getFIOMaxLengthValidation = (props?: {
    maxLength?: number;
    message?: string;
}): [number, string] => {
    return getFIOLengthValidation({
        length: props?.maxLength,
        message: props?.message,
        isMax: true,
    });
};

/**
 * Валидация на минимальное кол-во символов для полей фамилии/имени.
 */
export const getFIOMinLengthValidation = (props?: {
    minLength?: number;
    message?: string;
}): [number, string] => {
    return getFIOLengthValidation({
        length: props?.minLength,
        message: props?.message,
        isMax: false,
    });
};
